import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchItems } from '../support/queries';
import { architectureTagIndex, itemsSharingTags, tagIdsFor, TagLink } from '../support/relatedByTag';
import { cyberAcademyTrainingUrl } from '../support/cyberAcademy';
import { ARCHITECTURE_GTM_IDS } from './gtm-architecture-hooks';

const STALE = 5 * 60 * 1000;

const useRows = <T,>(key: string, endpoint: string, enabled = true) =>
  useQuery<T[]>({
    queryKey: ['related-by-tag', key],
    queryFn: async () => {
      const data = await fetchItems(endpoint);
      return Array.isArray(data) ? (data as T[]) : [];
    },
    enabled,
    staleTime: STALE,
    retry: false,
    refetchOnWindowFocus: false,
  });

export interface RelatedJourneyMap {
  id: number;
  name: string;
  description: string;
  typeName: string;
  /** Cyber Academy training page, which opens in a new tab. */
  href: string;
  sharedTagIds: number[];
}

export interface RelatedArchitecture {
  id: number;
  name: string;
  description: string;
  /** In-app route of the architecture page. */
  to: string;
  sharedTagIds: number[];
}

/** The route of an architecture's page: its own page when it has one, else the generic detail page. */
export const architectureRoute = (gtmId: number): string => {
  const slug = Object.keys(ARCHITECTURE_GTM_IDS).find((s) => ARCHITECTURE_GTM_IDS[s] === gtmId);
  return slug ? `/${slug}` : `/solutions/architecture/${gtmId}`;
};

/** Every architecture's tags (its own plus its use cases' and components'), loaded only when `enabled`. */
const useArchitectureTags = (enabled: boolean) => {
  const gtmLinks = useRows<TagLink>('gtm-links', 'db/go-to-market-to-tag/', enabled);
  const useCases = useRows<any>('use-cases', 'db/security-use-case/', enabled);
  const useCaseLinks = useRows<TagLink>('use-case-links', 'db/security-use-case-to-tag/', enabled);
  const components = useRows<any>('components', 'db/solution-component/', enabled);
  const componentLinks = useRows<TagLink>('component-links', 'db/solution-component-to-tag/', enabled);

  const index = useMemo(
    () =>
      architectureTagIndex({
        gtmLinks: gtmLinks.data,
        useCases: useCases.data,
        useCaseLinks: useCaseLinks.data,
        components: components.data,
        componentLinks: componentLinks.data,
      }),
    [gtmLinks.data, useCases.data, useCaseLinks.data, components.data, componentLinks.data],
  );
  const isLoading = [gtmLinks, useCases, useCaseLinks, components, componentLinks].some((q) => q.isLoading);
  return { index, isLoading };
};

/** Published journey maps that share a tag with the architecture `gtmId`. */
export function useJourneyMapsForArchitecture(gtmId: number | null | undefined) {
  const enabled = gtmId != null;
  const arch = useArchitectureTags(enabled);
  const mapLinks = useRows<TagLink>('map-links', 'db/journey-map-to-tag/', enabled);
  const maps = useRows<any>('maps', 'db/journey-map/', enabled);

  const data = useMemo<RelatedJourneyMap[]>(() => {
    const tags = Array.from(arch.index.get(Number(gtmId)) ?? []);
    const shared = itemsSharingTags(mapLinks.data, 'journey_map', tags);
    return (maps.data ?? [])
      .filter((m) => shared.has(Number(m.id)) && m.publication_status !== 'archived' && m.is_active !== false)
      .map((m) => ({
        id: Number(m.id),
        name: m.name,
        description: m.description || '',
        typeName: m.journey_type_name || '',
        href: cyberAcademyTrainingUrl(m.id) as string,
        sharedTagIds: shared.get(Number(m.id)) ?? [],
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [arch.index, mapLinks.data, maps.data, gtmId]);

  return { data, isLoading: arch.isLoading || mapLinks.isLoading || maps.isLoading };
}

/** Architectures that share a tag with the journey map `mapId`. */
export function useArchitecturesForJourneyMap(mapId: number | null | undefined) {
  const enabled = mapId != null;
  const arch = useArchitectureTags(enabled);
  const mapLinks = useRows<TagLink>('map-links', 'db/journey-map-to-tag/', enabled);
  const gtms = useRows<any>('gtms', 'db/go-to-market/', enabled);

  const data = useMemo<RelatedArchitecture[]>(() => {
    const mine = new Set(tagIdsFor(mapLinks.data, 'journey_map', mapId));
    return (gtms.data ?? [])
      .map((g) => ({ g, shared: Array.from(arch.index.get(Number(g.id)) ?? []).filter((t) => mine.has(t)) }))
      .filter(({ g, shared }) => g.type === 'architecture' && shared.length > 0)
      .map(({ g, shared }) => ({
        id: Number(g.id),
        name: g.name,
        description: g.description || '',
        to: architectureRoute(Number(g.id)),
        sharedTagIds: shared,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [arch.index, mapLinks.data, gtms.data, mapId]);

  return { data, isLoading: arch.isLoading || mapLinks.isLoading || gtms.isLoading };
}
